import React, { Component, Suspense } from "react";
import { Container } from "@sainsburys-tech/fable";
import { Routes, Route, Navigate, BrowserRouter, useParams } from "react-router-dom";
import PropTypes from "prop-types";

import applicationInsights from "../../utils/application-insights";

import * as DefaultRoutes from "../../utils/defaultRoutes";
import EnabledFeaturesContext from "../../context/enabledFeaturesContext";
import UserRolesContext from "../../context/userRolesContext";
import { ToastProvider } from "../../context/toastContext";
import { OverrideToastProvider } from "../../context/overrideToastContext";
import { ManagersOverrideToastProvider } from "../../context/managersOverrideToastContext";
import { BannerProvider } from "../../context/bannerContext";
import * as roles from "../../utils/roles";
import WhenAuthorized from "../WhenAuthorized";
import StoresContext from "../../context/storesContext";

import BackToTopButton from "../BackToTopButton";
import NavigationBar from "../NavigationBar/NavigationBar.js";
import NotFoundView from "../NotFoundView";
import AccessDeniedView from "../AccessDeniedView";
import LoadingView from "../LoadingView";
import ErrorView from "../ErrorView";
import lazyWithReload from "../../utils/lazyWithReload";

import ScrollToTop from "../ScrollToTop";
import { PageView } from "../Tracking";

import "./App.scss";

const AlertsPage = lazyWithReload(() => import("../AlertsPage"));
const AlertDetailsPage = lazyWithReload(() => import("../AlertDetailsPage"));
const DeviceDetailsPage = lazyWithReload(() => import("../DeviceDetailsPage"));
const StoreDetailsPage = lazyWithReload(() => import("../StoreDetailsPage"));
const StoresPage = lazyWithReload(() => import("../StoresPage"));
const StoreZoneSceneLevelsPage = lazyWithReload(() => import("../StoreZoneSceneLevelsPage"));
const StoreZoneConfigurePage = lazyWithReload(() => import("../StoreZoneConfigurePage"));
const StoreCommissioningPage = lazyWithReload(() => import("../StoreCommissioningPage"));
const StoreOverridePage = lazyWithReload(() => import("../StoreOverridePage"));
const OverridesPage = lazyWithReload(() => import("../OverridesPage"));
const OverridesGroupedListPage = lazyWithReload(() => import("../OverridesGroupedListPage"));
const OverrideEdit = lazyWithReload(() => import("../OverrideEdit"));
const LightingStatusPage = lazyWithReload(() => import("../LightingStatusPage"));

applicationInsights.initialise();

class PageErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? <ErrorView /> : this.props.children;
  }
}

const PageContainer = ({ children }) => (
  <Container style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem', marginTop: '-2rem' }}>
    <PageErrorBoundary>
      <Suspense fallback={<LoadingView />}>{children}</Suspense>
    </PageErrorBoundary>
  </Container>
);

// React Router v6: protected route wrappers
const LightingSetupRoute = ({ component: RouteComponent }) => (
  <WhenAuthorized roles={[roles.LIGHTING_SETUP]} fallback={<AccessDeniedView />}>
    <RouteComponent />
  </WhenAuthorized>
);

const LightingOverridesReadRoute = ({ component: RouteComponent }) => (
  <WhenAuthorized roles={[roles.LIGHTING_OVERRIDES_READ]} fallback={<AccessDeniedView />}>
    <RouteComponent />
  </WhenAuthorized>
);

const LightingOverridesWriteRoute = ({ component: RouteComponent }) => (
  <WhenAuthorized
    roles={[roles.LIGHTING_OVERRIDES_WRITE, roles.LIGHTING_OVERRIDES_READ]}
    fallback={<AccessDeniedView />}
  >
    <RouteComponent />
  </WhenAuthorized>
);

const AdminRoute = ({ component: RouteComponent }) => (
  <WhenAuthorized roles={[roles.ADMIN]} fallback={<AccessDeniedView />}>
    <RouteComponent />
  </WhenAuthorized>
);

// Wrapper to inject storeId param for StoreDetailsPage key remount
const StoreDetailsPageWrapper = () => {
  const { storeId } = useParams();
  return <StoreDetailsPage key={storeId} storeId={storeId} />;
};

// Wrapper to inject deviceId param for DeviceDetailsPage
const DeviceDetailsPageParamsWrapper = () => {
  const { deviceId } = useParams();
  return <DeviceDetailsPage deviceId={deviceId} />;
};

// Redirect for /lighting/stores/:storeId/commissioning → .../zones
const StoreCommissioningRedirect = () => {
  const { storeId } = useParams();
  return <Navigate to={`/lighting/stores/${storeId}/commissioning/zones`} replace />;
};

class App extends Component {
  componentDidMount() {
    PageView();
  }

  render() {
    return (
      <EnabledFeaturesContext.Provider value={this.props.enabledFeatures}>
        <UserRolesContext.Provider value={this.props.userRoles}>
          <StoresContext.Provider
            value={{
              stores: this.props.stores,
              onLightingStoresChange: this.props.onLightingStoresChange
            }}
          >
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop>
                <NavigationBar>
                  <BannerProvider>
                    <ToastProvider autoDismissTimeout={10000}>
                      <OverrideToastProvider>
                        <ManagersOverrideToastProvider>
                          <PageContainer>
                            <Routes>
                              <Route
                                path="/"
                                element={<Navigate to={DefaultRoutes.DEFAULT_ALERT_ROUTE} replace />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/zones/:zoneId/scene-levels"
                                element={<LightingSetupRoute component={StoreZoneSceneLevelsPage} />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/zones/add"
                                element={<LightingSetupRoute component={StoreZoneConfigurePage} />}
                              />
                              <Route
                                path="/lighting/overrides/add"
                                element={<LightingOverridesWriteRoute component={StoreOverridePage} />}
                              />
                              <Route
                                path="/lighting/overrides/:overrideId/edit"
                                element={<LightingOverridesWriteRoute component={OverrideEdit} />}
                              />
                              <Route
                                path="/lighting/overrides/:overrideId"
                                element={<LightingOverridesWriteRoute component={OverridesGroupedListPage} />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/zones/:zoneId/configuration"
                                element={<LightingSetupRoute component={StoreZoneConfigurePage} />}
                              />
                              <Route
                                path="/stores/:storeId"
                                element={<StoreDetailsPageWrapper />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/commissioning"
                                element={<StoreCommissioningRedirect />}
                              />
                              <Route
                                path="/lighting/stores/:storeId/commissioning/*"
                                element={<LightingSetupRoute component={StoreCommissioningPage} />}
                              />
                              <Route path="/stores" element={<StoresPage />} />
                              <Route path="/alerts" element={<AlertsPage />} />
                              <Route
                                path="/alerts/:alertId"
                                element={<AlertDetailsPage />}
                              />
                              <Route
                                path="/devices/:deviceId"
                                element={<DeviceDetailsPageParamsWrapper />}
                              />
                              <Route
                                path="/lighting/overrides"
                                element={<LightingOverridesReadRoute component={OverridesPage} />}
                              />
                              <Route
                                path="/lighting/status"
                                element={<AdminRoute component={LightingStatusPage} />}
                              />
                              <Route path="*" element={<NotFoundView />} />
                            </Routes>
                          </PageContainer>
                        </ManagersOverrideToastProvider>
                      </OverrideToastProvider>
                    </ToastProvider>
                  </BannerProvider>
                </NavigationBar>
              </ScrollToTop>
            </BrowserRouter>
            <BackToTopButton />
          </StoresContext.Provider>
        </UserRolesContext.Provider>
      </EnabledFeaturesContext.Provider>
    );
  }
}

App.propTypes = {
  enabledFeatures: PropTypes.array
};

export default App;
